/**
 * mergeOnReceive — the adoption-time decision for an incoming Firestore
 * snapshot. Walter 2026-08-07, sync-hardening round; plan:
 * `docs/proposals/2026-07-19-sync-hardening-merge-on-receive.md`.
 *
 * WHY THIS EXISTS. The panels' snapshot-sync effect used to adopt the server
 * value by overwriting `localContent` unconditionally (once the #157 sticky
 * gate + #162 retry let it through). That's correct when only ONE side moved,
 * but when the server moved AND our buffer has diverged — unflushed offline
 * edits, a save that never landed, two devices typing at once — the overwrite
 * discards the local edits with no snapshot. That was the last remaining
 * "data gone" path, and the write path can't see it: only the RECEIVE side
 * knows both versions.
 *
 * This module is deliberately PURE and tiny. The panels' sync effect is the
 * most-burned code in the app (#136, #157, #162), so the thinking lives here
 * (unit-testable, no React, no Firestore) and the effect only orchestrates:
 * ask, then apply. `threeWayMerge` does the actual line merging and its
 * semantics are NOT touched here (#159's keep-both insert rule stands).
 *
 * Callers pass all three values in the SAME space — the panels use the
 * display-stripped text (what the user sees and what a save would write), so
 * the display-only recurrence-glyph strip can never read as a divergence.
 */

import { mergeThreeWay } from './threeWayMerge';

/**
 * How long a version we've already moved past is treated as a stale ECHO
 * rather than a genuine revert. See isStaleEcho.
 *
 * 60s is generous for a snapshot that lost a race with our own write (those
 * arrive within a second or two) and short enough that a person deliberately
 * undoing on another device — where the same text can legitimately come back
 * — is not blocked for any length of time.
 */
export const STALE_ECHO_MS = 60 * 1000;

/**
 * isStaleEcho(server, history, now) — is this incoming version one we have
 * ALREADY moved past?
 *
 * WHY (Walter 2026-09-08, and this is the one that was eating his text). He
 * reproduced a loss on dev with the content trail on. Three log lines, same
 * second:
 *
 *   22:34:01  saved          w=h35m1   chars=268
 *   22:34:01  adopt-decision (panel B) base=243 local=243 server=268
 *   22:34:01  adopt-decision w=h35m1   base=268 local=268 server=243
 *
 * His window wrote 268, then adopted 243 — the text from before he typed —
 * and the line was gone. Eleven minutes earlier the same list went
 * 214 → 240 → 214.
 *
 * The cause is the `l === b` branch below, whose reasoning is *"our buffer is
 * exactly what we last saved, so there are no local edits to protect."* True
 * only if the incoming version is NEWER. decideAdoption compares three
 * strings and has no notion of order, so it cannot tell the server moving
 * FORWARD from the server moving BACKWARD past our own write. Handed a
 * cache-served snapshot that lost a race with our save, it adopts the past.
 *
 * Timestamps were the obvious signal and they are unusable at exactly the
 * moment we need them: a document we have just written carries a pending
 * `serverTimestamp`, i.e. null, until the server confirms it. So instead the
 * panel remembers the versions it has held — the text it adopted, merged or
 * wrote — and an incoming version matching one it has already left behind,
 * recently, is an echo rather than news.
 *
 * `history` is oldest-first, newest LAST, each `{ text, at }`. The newest
 * entry is what we currently hold and is never an echo. The time bound is
 * what keeps a deliberate revert on another device from being ignored
 * forever: come back to the same text a minute later and we take it.
 */
export function isStaleEcho(server, history, now = Date.now(), fromSelf = false) {
  if (!Array.isArray(history) || history.length < 2) return false;
  const s = String(server ?? '');
  // Everything except the newest entry — the newest IS our current version,
  // and the server agreeing with it is the ordinary case, not an echo.
  for (let i = 0; i < history.length - 1; i++) {
    const h = history[i];
    if (!h || typeof h.text !== 'string') continue;
    if (h.text !== s) continue;
    // Walter 2026-10-12: OUR OWN write coming back is an echo at ANY age.
    // His PC saved 30917 at 14:36:07 and adopted that same text back at
    // 14:37:11 - 64 seconds later, four seconds past this window - which
    // walked the window back onto older text. It then saved that over the
    // phone's newer version and six lines he had just typed were gone
    // (content trail, 14:51:36). The clock was never the right test for our
    // own text: the list doc says who wrote it, so when that is US and we
    // have already moved past it, no amount of elapsed time makes it news.
    //
    // The time bound still governs another DEVICE's write, where coming back
    // to earlier text can be a deliberate revert that we must adopt. Raising
    // the bound instead was considered and rejected: it would ignore such a
    // revert for the whole window.
    if (fromSelf) return true;
    if (now - h.at > STALE_ECHO_MS) return false; // old enough to be a real revert
    return true;
  }
  return false;
}

/**
 * decideAdoption({ base, local, server }) → { action, content, ... }
 *
 *   base   — the server content our buffer descends from (baseContentRef).
 *   local  — what the editor's buffer currently holds.
 *   server — the content that just arrived in the snapshot.
 *
 *   history — versions this panel has held, oldest first, `{ text, at }`.
 *             Optional: omit it and the ordering guard is simply off.
 *   fromSelf — did THIS client write the version that just arrived?
 *             (`list.lastWriteByClientId === getClientId()`). Our own text
 *             coming back is an echo however long it took.
 *   now     — injectable clock, for tests.
 *
 * Actions:
 *   'stale'    — the incoming version is one we have already moved past, so
 *                it is an echo, not news. `content` is the LOCAL buffer:
 *                change nothing, and leave `base` where it is.
 *   'adopt'    — take `content` (= server) verbatim. Today's behaviour, and
 *                the overwhelmingly common path: either the server didn't
 *                move, or we never diverged, or we already agree.
 *   'merge'    — three-way divergence that merged CLEANLY. `content` is the
 *                merged text: adopt it AND write it back so the other device
 *                converges on it too.
 *   'conflict' — three-way divergence with a genuine same-line clash.
 *                `content` is the server value (what to display),
 *                `divergedLocal` is the buffer that must be archived BEFORE
 *                it's dropped, and `conflicts` counts the clashing regions.
 */
export function decideAdoption({ base, local, server, history, now, fromSelf = false }) {
  const b = String(base ?? '');
  const l = String(local ?? '');
  const s = String(server ?? '');

  // THE SERVER MOVED BACKWARDS, PAST SOMETHING WE ALREADY HELD. Walter
  // 2026-09-08 — the branch that was losing his text. Nothing below can catch
  // this: with `l === b` (we just saved, buffer matches base) the adopt branch
  // takes the older server value verbatim and the new line is gone. Checked
  // FIRST because a version we've already left behind cannot inform any of the
  // decisions below, whatever they'd otherwise conclude.
  if (isStaleEcho(s, history, now, fromSelf)) {
    return { action: 'stale', content: l, conflicts: 0 };
  }

  // THE SERVER SAID NOTHING NEW. Walter 2026-08-22: this used to return
  // 'adopt', and that quietly destroyed unsaved work — he typed a line on the
  // PC, paused to think before pressing Enter, and it vanished. The effect
  // re-runs for reasons other than new content (a reminders update, the 11s
  // retry after a typing pause), so with the server unchanged and his buffer
  // holding an unsaved line, "adopt" overwrote the buffer with the server's
  // older text. There is nothing to gain by adopting here and unsaved edits to
  // lose, so KEEP the buffer instead. The plan this was built from carried the
  // same hole; it isn't only a coding slip.
  if (s === b) {
    return { action: 'keep', content: s, conflicts: 0 };
  }

  // At most one side moved — nothing to reconcile.
  //   l === b : our buffer is exactly what we last adopted/saved — no local
  //             edits to protect, so the server value simply wins.
  //   l === s : we already agree with the server.
  if (l === b || l === s) {
    return { action: 'adopt', content: s, conflicts: 0 };
  }

  // Both sides moved. mergeThreeWay(base, mine, theirs) — "mine" is our
  // buffer, "theirs" is the server (which is why disjoint inserts land
  // server-first ≈ chronological, per #159).
  const { merged, clean, conflicts } = mergeThreeWay(b, l, s);
  if (clean) {
    return { action: 'merge', content: merged, conflicts: 0 };
  }
  return { action: 'conflict', content: s, divergedLocal: l, conflicts };
}

export default decideAdoption;
